import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInicisPayment } from '../../payment/hooks/useInicisPayment';
import { useAddUserIdentity } from '../../../domains/user/ui/hooks/useAddUserIdentity';
import type { OrderProductType } from '../../../domain/repositories/PaymentRepository';
import type {
  addUserIdentityBody,
  addUserIdentityResult
} from '../../../domains/user/services/userIdentities';
import { ROUTES } from '../../../constants/routes';
import { IDENTITY_VERIFICATION_COMPLETE } from '../constants/identityVerification';

const SCENE_GO_TO_SETTING_TIME = 1500;

const hasNestedError = (e: unknown): e is { error: { code: string; message: string } } => {
  return typeof e === 'object' && e !== null && 'error' in e;
};

function parseOrderProductType(value: string | null): OrderProductType | undefined {
  if (value === 'bettie_product' || value === 'scene_product' || value === 'product') return value;
  return undefined;
}

export function useIdentityVerificationRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentQueryParams = new URLSearchParams(location.search);
  const redirectPath = currentQueryParams.get('redirect_path');
  const impUid = currentQueryParams.get('imp_uid');
  const merchantUid = currentQueryParams.get('merchant_uid');
  const initialSuccess = currentQueryParams.get('success') === 'true';

  const [isSuccess, setIsSuccess] = useState(initialSuccess);
  const [isDuplicate, setIsDuplicate] = useState(false);

  const { amount, productName, productId, productType, sceneId, isBilling, type } = (() => {
    const topAmount = currentQueryParams.get('amount');
    const topProductName = currentQueryParams.get('productName');
    const topProductId = currentQueryParams.get('productId');
    const topProductType = currentQueryParams.get('productType');
    const topSceneId = currentQueryParams.get('sceneId');
    const topIsBilling = currentQueryParams.get('isBilling');
    const topType = currentQueryParams.get('type');

    if (topAmount || topProductName || topProductId || topType) {
      return {
        amount: topAmount,
        productName: topProductName,
        productId: topProductId,
        productType: topProductType,
        sceneId: topSceneId,
        isBilling: topIsBilling === 'true',
        type: topType
      };
    }

    if (redirectPath) {
      const decoded = decodeURIComponent(redirectPath);
      const url = new URL(decoded, window.location.origin);
      const sp = url.searchParams;

      return {
        amount: sp.get('amount'),
        productName: sp.get('productName'),
        productId: sp.get('productId'),
        productType: sp.get('productType'),
        sceneId: sp.get('sceneId'),
        isBilling: sp.get('isBilling') === 'true',
        type: sp.get('type')
      };
    }

    return {
      amount: null,
      productName: null,
      productId: null,
      productType: null,
      sceneId: null,
      type: null
    };
  })();

  const { mutateAsync: addUserIdentity } = useAddUserIdentity();

  const { handleCreateOrder } = useInicisPayment({
    amount: amount ?? '',
    productName: productName ?? '',
    productId: productId ?? '',
    productType: parseOrderProductType(productType),
    sceneId: sceneId || undefined
  });

  useEffect(() => {
    if (isSuccess && impUid && merchantUid) {
      (async () => {
        try {
          const { data, error }: addUserIdentityResult = await addUserIdentity({
            imp_uid: impUid,
            merchant_uid: merchantUid
          } as addUserIdentityBody);
          if (error) {
            setIsSuccess(false);
            const nestedCode = hasNestedError(error) ? error.error.code : undefined;
            const nestedMessage = hasNestedError(error) ? error.error.message : undefined;

            if (nestedCode === '1002') {
              setIsDuplicate(true);
            }
            if (
              nestedCode === 'RecordNotUnique' &&
              nestedMessage === 'This key already identified'
            ) {
              setIsDuplicate(true);
            }
            return;
          }
          if (data?.userIdentity?.userIdentityId) {
            setIsSuccess(true);
            if (window.opener) {
              window.opener.postMessage(
                { type: IDENTITY_VERIFICATION_COMPLETE },
                window.location.origin
              );
            }
          }
        } catch {
          setIsSuccess(false);
        }
      })();
    }
  }, [isSuccess, impUid, merchantUid, addUserIdentity]);

  useEffect(() => {
    if (type === 'scene' && isSuccess) {
      const timer = setTimeout(() => {
        if (redirectPath && window.opener) {
          window.close();
        } else if (redirectPath) {
          navigate(decodeURIComponent(redirectPath), { replace: true });
        } else {
          navigate(ROUTES.SCENE.SETTING, { replace: true });
        }
      }, SCENE_GO_TO_SETTING_TIME);
      return () => clearTimeout(timer);
    }
  }, [type, isSuccess, navigate, redirectPath]);

  const hasRequiredParams = Boolean(impUid && merchantUid);

  const handleClick = useCallback(async () => {
    if (isSuccess) {
      if (type === 'invite') {
        navigate('/free-charge/invite', { replace: true });
        return;
      }

      if (isBilling) {
        navigate(
          `/payment/billing?amount=${amount}&productName=${productName}&productId=${productId}`
        );
        return;
      }
      await handleCreateOrder();
    } else {
      if (type === 'scene') {
        navigate(ROUTES.SCENE.INTRO, { replace: true });
        return;
      }
      navigate(redirectPath ? decodeURIComponent(redirectPath) : '/bettie-chat-list', {
        replace: true
      });
    }
  }, [
    isSuccess,
    handleCreateOrder,
    navigate,
    redirectPath,
    amount,
    productName,
    productId,
    isBilling,
    type
  ]);

  return {
    isSuccess,
    hasRequiredParams,
    handleClick,
    isDuplicate,
    type
  };
}
